import { createFileRoute } from '@tanstack/react-router'
import { LegalLayout } from '@/components/layout/legal-layout'
import { breadcrumbSchema, seo } from '@/shared/lib/seo'

export const Route = createFileRoute('/privacy')({
  head: () => ({
    ...seo({
      title: 'Privacy Policy | ControlStack.ai',
      description:
        'How controlstack.ai collects, uses and protects your data, including your GDPR rights and how to exercise them. Operated by Daza Limited.',
      path: '/privacy',
      jsonLd: [breadcrumbSchema('Privacy Policy', '/privacy')],
    }),
  }),
  component: PrivacyPolicy,
})

function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="January 23, 2026">
      <section>
        <h2>1. Introduction</h2>
        <p>
          controlstack.ai ("we", "our", or "us") is committed to protecting your
          privacy. This Privacy Policy explains how we collect, use, disclose,
          and safeguard your information when you visit our website and use our
          services.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        <h3>Personal Information</h3>
        <p>
          We may collect personal information that you voluntarily provide,
          including:
        </p>
        <ul>
          <li>Name and email address when you subscribe to our newsletter</li>
          <li>Payment information when you purchase courses or services</li>
          <li>Profile information when you create an account</li>
          <li>Communications when you contact us</li>
        </ul>

        <h3>Automatically Collected Information</h3>
        <p>When you visit our website, we may automatically collect:</p>
        <ul>
          <li>Device and browser information</li>
          <li>IP address and location data</li>
          <li>Pages visited and time spent on site</li>
          <li>Referring website addresses</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve our services</li>
          <li>Send you newsletters, updates, and marketing communications</li>
          <li>Process transactions and send related information</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Monitor and analyze usage patterns and trends</li>
          <li>Detect, prevent, and address technical issues</li>
        </ul>
      </section>

      <section>
        <h2>4. Legal Basis for Processing (GDPR)</h2>
        <p>
          If you are in the European Economic Area (EEA), our legal basis for
          processing your personal data includes:
        </p>
        <ul>
          <li>
            <strong>Consent:</strong> You have given us permission to process
            your data for specific purposes
          </li>
          <li>
            <strong>Contract:</strong> Processing is necessary to fulfill our
            contractual obligations to you
          </li>
          <li>
            <strong>Legitimate Interests:</strong> Processing is necessary for
            our legitimate business interests
          </li>
          <li>
            <strong>Legal Obligation:</strong> Processing is necessary to comply
            with legal requirements
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Your Rights</h2>
        <p>Under GDPR and other applicable laws, you have the right to:</p>
        <ul>
          <li>
            <strong>Access:</strong> Request copies of your personal data
          </li>
          <li>
            <strong>Rectification:</strong> Request correction of inaccurate
            data
          </li>
          <li>
            <strong>Erasure:</strong> Request deletion of your personal data
          </li>
          <li>
            <strong>Restrict Processing:</strong> Request limitation of data
            processing
          </li>
          <li>
            <strong>Data Portability:</strong> Request transfer of your data
          </li>
          <li>
            <strong>Object:</strong> Object to processing of your personal data
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Withdraw previously given consent
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Data Retention</h2>
        <p>
          We retain your personal data only for as long as necessary to fulfill
          the purposes outlined in this Privacy Policy, unless a longer
          retention period is required or permitted by law.
        </p>
      </section>

      <section>
        <h2>7. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to
          protect your personal data against unauthorized access, alteration,
          disclosure, or destruction. However, no method of transmission over
          the Internet is 100% secure.
        </p>
      </section>

      <section>
        <h2>8. Third-Party Services</h2>
        <p>
          We may share your information with third-party service providers for:
        </p>
        <ul>
          <li>Payment processing</li>
          <li>Email delivery services</li>
          <li>Analytics services</li>
          <li>Hosting and infrastructure</li>
        </ul>
      </section>

      <section>
        <h2>9. International Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries
          other than your own. We ensure appropriate safeguards are in place to
          protect your data in accordance with this Privacy Policy.
        </p>
      </section>

      <section>
        <h2>10. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or wish to exercise
          your rights, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> privacy@controlstack.ai
        </p>
      </section>
    </LegalLayout>
  )
}
